import { useState, useEffect, useMemo } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { useAuth } from '@/context/AuthContext';
import { useUserConfigQuery } from '@/hooks/useUserConfigQuery';
import type { ClientDisplayData } from '@/pages/ClientsPage';
import {
  type ClientFormData,
  initializeFormDataFromDefaults
} from '../utilities/formDataUtils';
import { detectCustomizedSections, detectGranularCustomizedFields } from '../utilities/customizationUtils';

interface UseClientFormStateProps {
  clientData: ClientFormData;
  isEditing: boolean;
  onNameChange?: (name: string) => void;
  allClients: ClientDisplayData[];
}

export const useClientFormState = ({
  clientData,
  isEditing,
  onNameChange,
}: UseClientFormStateProps) => {
  const { user } = useAuth();
  const { data: userDefaults, isLoading: isLoadingDefaults } = useUserConfigQuery(user?.id);

  const [formData, setFormData] = useState<ClientFormData>({});
  const [debouncedFormData] = useDebouncedValue(formData, 300);
  const [isTravelManuallyEnabled, setIsTravelManuallyEnabled] = useState(false);
  const [isAiGeneratedDescription, setIsAiGeneratedDescription] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (isEditing) {
      const dataWithDerived = { ...clientData };
      if (dataWithDerived.fixed_fee_schedule && dataWithDerived.fixed_fee_schedule.length > 0) {
        dataWithDerived.fixed_fee_amount = dataWithDerived.fixed_fee_schedule[0].fee_amount;
      } else if (dataWithDerived.fixed_fee_schedule) {
        dataWithDerived.fixed_fee_amount = null;
      }
      setFormData(dataWithDerived);
      onNameChange?.(clientData.name ?? '');
    } else if (userDefaults) {
      const initialData = initializeFormDataFromDefaults(clientData, userDefaults);
      setFormData(initialData);
      onNameChange?.(initialData.name ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onNameChange is a callback prop that may change identity; only re-initialize when source data changes
  }, [clientData, userDefaults, isEditing]);

  // Compute granular customized fields
  const granularCustomizedFields = useMemo((): Record<string, boolean> => {
    if (!userDefaults) {
      return {};
    }

    return detectGranularCustomizedFields(debouncedFormData, userDefaults);
  }, [debouncedFormData, userDefaults]);

  // Compute customized sections
  const customizedSections = useMemo(() => {
    if (!userDefaults) {
      return {
        billing: false,
        breaks: false,
        invoicing: false,
        roles: false,
        multipliers: false,
        shortTurn: false,
        holiday: false,
        mealBreak: false,
        overnight: false,
        expenses: false,
        gear: false,
      };
    }
    return detectCustomizedSections(debouncedFormData, userDefaults);
  }, [debouncedFormData, userDefaults]);

  return {
    formData,
    setFormData,
    isTravelManuallyEnabled,
    setIsTravelManuallyEnabled,
    isAiGeneratedDescription,
    setIsAiGeneratedDescription,
    userDefaults,
    isLoadingDefaults,
    granularCustomizedFields,
    customizedSections,
  };
};
